import React, { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';

import {
  createPolicyStore,
  PolicyConfig,
  PolicyStore,
  PolicyStoreApi,
  PolicyTemplateProps,
} from './createStore';

const PolicyContext = createContext<PolicyStoreApi | null>(null);

type PolicyProviderProps = React.PropsWithChildren<{
  template: PolicyTemplateProps;
  config: PolicyConfig;
  orgId: string;
}>;

export const PolicyProvider = React.memo(
  ({ children, template, config, orgId, ...props }: PolicyProviderProps) => {
    const storeRef = useRef<PolicyStoreApi | null>(null);
    if (!storeRef.current) {
      storeRef.current = createPolicyStore({ template, config, orgId });
    }
    React.useEffect(() => {
      storeRef.current = createPolicyStore({ template, config, orgId });
    }, [template, orgId, config]);
    return (
      <PolicyContext.Provider value={storeRef.current} {...props}>
        {children}
      </PolicyContext.Provider>
    );
  }
);

export function usePolicyContext() {
  const store = useContext(PolicyContext);
  if (!store) {
    throw new Error('usePolicyContext must be used within a PolicyProvider');
  }
  return store;
}
/*
 * 如果直接在组件中使用store，需要用useStore包装一下，这里省去了这个步骤，直接填入selector就好
 * action, config 从storeSelector中获取
 */
export function storeSelector(store: PolicyStoreApi) {
  if (!store) {
    throw new Error('store should  not be null');
  }
  return function <T>(
    selector: (state: PolicyStore) => T,
    equalityFn?: (left: T, right: T) => boolean
  ) {
    return useStore(store, selector, equalityFn);
  };
}
/*
 *  此处的selector是针对policy的，使组件中只关注policy的结构
 *  policy从templateSelector中获取
 *  但如果需要使用action，那就需要用storeSelector
 */
export function templateSelector(store: PolicyStoreApi) {
  if (!store) {
    throw new Error('store should  not be null');
  }
  return function <TemplateProps extends PolicyTemplateProps, T = any>(
    selector: (state: TemplateProps) => T,
    equalityFn?: (left: T, right: T) => boolean
  ) {
    const newSelector = (state: PolicyStore) =>
      selector(state.template as TemplateProps);
    return useStore(store, newSelector, equalityFn);
  };
}

export * from './selectors';
export * from './createStore';
